/**
 * MCP tool creation flow (#148) — Studio source-aware tool creation.
 *
 * Wizard flow:
 *   Step 1: Pick MCP server from tenant's registered MCP installs.
 *           (Read-only — if zero servers, the user is directed to
 *            Connections to register one first; we don't try to
 *            embed the registration form here.)
 *   Step 2: Fetch the server's resolved tool manifest and let the
 *           user multi-select tools to import.
 *   Step 3: Per-tool friendly name + policy. One submit creates
 *           N `workspace_tools` rows with `tool_source='mcp'`.
 *
 * Why a separate component (vs reusing tools.new.tsx):
 *   - The MCP flow has no Monaco editor, no HTTP body, no Zod
 *     parameter editor. Tool spec is inherited from the MCP
 *     manifest. Trying to graft the wizard onto the existing
 *     single-page form would balloon the conditionals past the
 *     point of maintainability and dilute the "Custom Function"
 *     flow's authoring UX.
 *   - Per the founder direction in W22E: each source has its own
 *     creation file under `components/studio/tools/`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, CheckCircle2, ExternalLink, Plug, RefreshCw, Search, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { PolicyBadge } from '@/components/studio/policy-badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { useCreateWorkspaceTool, type ToolPolicy } from '@/lib/use-workspace-tools';
import { useQuery } from '@tanstack/react-query';
import { POLICY_DESCRIPTIONS } from '@/components/tools-authoring-shared/tools-shared.constants';

// ─── Types ──────────────────────────────────────────────────────────

interface McpServerListItem {
  id: string;
  systemServerId: string | null;
  displayName: string;
  description: string;
  namespace: string;
  tier: string;
  status: string;
  isHealthy: boolean;
  toolCount: number;
  tools: McpManifestTool[];
}

interface McpManifestTool {
  name: string;
  description?: string;
  inputSchema?: unknown;
  outputSchema?: unknown;
  isWriteOperation?: boolean;
  permissionLevel?: string;
  /** Whatever else the adapter normalized — passed through. */
  [k: string]: unknown;
}

interface McpServerListResponse {
  data: McpServerListItem[];
}

// ─── Component ──────────────────────────────────────────────────────

interface Props {
  onCancel: () => void;
  onBack: () => void;
}

type Step = 'pick-server' | 'pick-tools' | 'configure';

interface ImportEntry {
  toolName: string;
  /** Generated slug (lowercase, underscored). User-editable. */
  slug: string;
  displayName: string;
  description: string;
  policy: ToolPolicy;
  spec: unknown;
}

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);

export function CreateFlowMcp({ onCancel, onBack }: Props) {
  const navigate = useNavigate();
  const createTool = useCreateWorkspaceTool();

  const [step, setStep] = useState<Step>('pick-server');
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null);
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set());
  const [entries, setEntries] = useState<ImportEntry[]>([]);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch tenant MCP installs.
  const serversQuery = useQuery<McpServerListResponse, Error>({
    queryKey: ['connections-mcp-servers'],
    queryFn: () =>
      apiClient.get<McpServerListResponse>('/connections-module/mcp'),
  });

  const servers = serversQuery.data?.data ?? [];
  const selectedServer = useMemo(
    () => servers.find((s) => s.id === selectedServerId) ?? null,
    [servers, selectedServerId],
  );

  // When the selected server changes, reset tool selection.
  useEffect(() => {
    setSelectedTools(new Set());
    setEntries([]);
  }, [selectedServerId]);

  const handleToggleTool = (toolName: string, checked: boolean) => {
    setSelectedTools((prev) => {
      const next = new Set(prev);
      if (checked) next.add(toolName);
      else next.delete(toolName);
      return next;
    });
  };

  const handleAdvanceToConfigure = () => {
    if (!selectedServer || selectedTools.size === 0) return;
    const prepared: ImportEntry[] = selectedServer.tools
      .filter((t) => selectedTools.has(t.name))
      .map((t) => ({
        toolName: t.name,
        slug: slugify(`${selectedServer.namespace}_${t.name}`),
        displayName: t.name,
        description:
          t.description ??
          `MCP tool ${t.name} imported from ${selectedServer.displayName}.`,
        // MCP tools default to require_approval when the manifest
        // marks them as write operations. Defense in depth — the
        // MCP gateway re-checks at invocation time, but the catalog
        // policy is what end-users see in the agent run-card.
        policy: t.isWriteOperation ? 'require_approval' : 'auto_approve',
        spec: {
          input: t.inputSchema ?? { type: 'object', properties: {}, required: [] },
          output: t.outputSchema ?? { type: 'object', properties: {} },
        },
      }));
    setEntries(prepared);
    setStep('configure');
  };

  const updateEntry = (idx: number, patch: Partial<ImportEntry>) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  const handleSubmit = async () => {
    if (!selectedServer) return;
    if (entries.length === 0) return;

    // Validate slugs aren't blank.
    const blank = entries.findIndex((e) => !e.slug.trim() || !e.displayName.trim());
    if (blank !== -1) {
      toast.error(`Row ${blank + 1}: slug and display name are required.`);
      return;
    }

    setSubmitting(true);
    let imported = 0;
    const failures: { slug: string; message: string }[] = [];
    for (const entry of entries) {
      try {
        await createTool.mutateAsync({
          name: entry.slug.trim(),
          displayName: entry.displayName.trim(),
          description: entry.description.trim() || `MCP tool ${entry.toolName}`,
          toolSource: 'mcp',
          spec: entry.spec,
          handlerConfig: {
            // Reference back to the tenant install so the runtime
            // gateway can resolve the executor. `serverId` is the
            // install id (UUID), `toolName` is the manifest entry.
            mcpInstallId: selectedServer.id,
            mcpServerNamespace: selectedServer.namespace,
            toolName: entry.toolName,
            kind: 'mcp',
          },
          namespace: 'ext',
          policy: entry.policy,
        });
        imported += 1;
      } catch (err) {
        const data = (err as { data?: { error?: { message?: string } } })?.data;
        const message =
          data?.error?.message ??
          (err instanceof Error ? err.message : 'Unknown error');
        failures.push({ slug: entry.slug, message });
      }
    }
    setSubmitting(false);

    if (failures.length === 0) {
      toast.success(
        imported === 1
          ? '1 MCP tool imported'
          : `${imported} MCP tools imported`,
      );
      navigate({ to: '/studio/tools' });
    } else {
      toast.error(`${imported} imported, ${failures.length} failed`, {
        description: failures
          .slice(0, 3)
          .map((f) => `${f.slug}: ${f.message}`)
          .join('\n'),
      });
    }
  };

  // ─── Render ────────────────────────────────────────────────────

  const renderHeader = (title: string, sub: string) => (
    <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => {
            if (step === 'pick-server') onBack();
            else if (step === 'pick-tools') setStep('pick-server');
            else setStep('pick-tools');
          }}
          aria-label="Back"
          title="Back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Separator orientation="vertical" className="h-5" />
        <div>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Plug className="h-4 w-4 text-blue-600" />
            Import MCP Tools
          </h1>
          <p className="text-xs text-muted-foreground">
            {title} · {sub}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        {step === 'pick-server' && (
          <Button
            size="sm"
            disabled={!selectedServerId}
            onClick={() => setStep('pick-tools')}
          >
            Next: pick tools <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        )}
        {step === 'pick-tools' && (
          <Button
            size="sm"
            disabled={selectedTools.size === 0}
            onClick={handleAdvanceToConfigure}
          >
            Next: configure ({selectedTools.size}){' '}
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        )}
        {step === 'configure' && (
          <Button
            size="sm"
            disabled={submitting || entries.length === 0}
            onClick={handleSubmit}
          >
            {submitting ? 'Importing…' : `Import ${entries.length} tool${entries.length === 1 ? '' : 's'}`}
          </Button>
        )}
      </div>
    </div>
  );

  if (step === 'pick-server') {
    return (
      <div className="flex flex-col h-full">
        {renderHeader('Step 1 of 3', 'Pick MCP server')}
        <div className="flex-1 overflow-auto p-6 max-w-4xl mx-auto w-full space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Choose a registered MCP server</CardTitle>
              <CardDescription className="text-xs">
                Servers come from Connections → MCP. Each row shows the
                manifest tool count and connection health.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {serversQuery.isLoading ? (
                <div className="py-10 text-center text-sm text-muted-foreground animate-pulse">
                  Loading MCP servers…
                </div>
              ) : serversQuery.error ? (
                <div className="py-6 text-sm text-destructive flex items-start gap-2">
                  <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>Failed to load MCP servers: {serversQuery.error.message}</div>
                </div>
              ) : servers.length === 0 ? (
                <div className="border border-dashed rounded-lg p-8 text-center space-y-3">
                  <Plug className="h-8 w-8 text-muted-foreground/40 mx-auto" />
                  <div className="text-sm font-medium">No MCP servers registered</div>
                  <p className="text-xs text-muted-foreground max-w-md mx-auto">
                    Register an MCP server in Connections first, then come
                    back here to import its tools into Studio.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => navigate({ to: '/connections' })}
                  >
                    Open Connections <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <RadioGroup
                  value={selectedServerId ?? ''}
                  onValueChange={(v) => setSelectedServerId(v)}
                  className="space-y-2"
                >
                  {servers.map((s) => (
                    <label
                      key={s.id}
                      htmlFor={`mcp-server-${s.id}`}
                      className={cn(
                        'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                        selectedServerId === s.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/30',
                      )}
                    >
                      <RadioGroupItem
                        id={`mcp-server-${s.id}`}
                        value={s.id}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{s.displayName}</span>
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-[10px]',
                              s.isHealthy
                                ? 'border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
                                : 'border-amber-500/30 text-amber-700 dark:text-amber-300',
                            )}
                          >
                            {s.status}
                          </Badge>
                          <Badge variant="secondary" className="text-[10px] font-mono">
                            {s.namespace}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {s.description || 'No description.'}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {s.toolCount} tool{s.toolCount === 1 ? '' : 's'} in manifest
                        </p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              )}
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => serversQuery.refetch()}
                  disabled={serversQuery.isFetching}
                >
                  <RefreshCw
                    className={cn(
                      'h-3.5 w-3.5',
                      serversQuery.isFetching && 'animate-spin',
                    )}
                  />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (step === 'pick-tools') {
    const tools = selectedServer?.tools ?? [];
    const q = search.toLowerCase();
    const filteredTools = q
      ? tools.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            (t.description ?? '').toLowerCase().includes(q),
        )
      : tools;

    return (
      <div className="flex flex-col h-full">
        {renderHeader('Step 2 of 3', `Pick tools from ${selectedServer?.displayName ?? ''}`)}
        <div className="flex-1 overflow-auto p-6 max-w-4xl mx-auto w-full space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">
                Manifest tools
                <span className="text-muted-foreground font-normal ml-2 text-xs">
                  ({selectedTools.size} selected · {tools.length} available)
                </span>
              </CardTitle>
              <CardDescription className="text-xs">
                Each selected tool becomes a row in the Studio tool catalog.
                You can rename them in the next step.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search manifest…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9 text-sm"
                />
              </div>
              {tools.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  This server has no tools in its manifest.
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() =>
                        setSelectedTools(new Set(tools.map((t) => t.name)))
                      }
                    >
                      Select all ({tools.length})
                    </button>
                    <button
                      type="button"
                      className="text-muted-foreground hover:underline"
                      onClick={() => setSelectedTools(new Set())}
                    >
                      Clear
                    </button>
                  </div>
                  {filteredTools.map((t) => {
                    const checked = selectedTools.has(t.name);
                    return (
                      <label
                        key={t.name}
                        htmlFor={`mcp-tool-${t.name}`}
                        className={cn(
                          'flex items-start gap-3 p-2.5 rounded-md border cursor-pointer transition-colors',
                          checked
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/30',
                        )}
                      >
                        <Checkbox
                          id={`mcp-tool-${t.name}`}
                          checked={checked}
                          onCheckedChange={(v) =>
                            handleToggleTool(t.name, v === true)
                          }
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-mono">{t.name}</span>
                            {t.isWriteOperation && (
                              <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-700 dark:text-amber-300">
                                Write
                              </Badge>
                            )}
                            {t.permissionLevel && (
                              <Badge variant="secondary" className="text-[10px]">
                                {t.permissionLevel}
                              </Badge>
                            )}
                          </div>
                          {t.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {t.description}
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // step === 'configure'
  return (
    <div className="flex flex-col h-full">
      {renderHeader('Step 3 of 3', 'Configure & import')}
      <div className="flex-1 overflow-auto p-6 max-w-4xl mx-auto w-full space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">
              Review and import — {entries.length} tool
              {entries.length === 1 ? '' : 's'}
            </CardTitle>
            <CardDescription className="text-xs">
              Each row creates a workspace_tools entry with{' '}
              <code className="font-mono">tool_source = mcp</code>. The
              runtime gateway routes invocations through the MCP install
              you picked.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {entries.map((e, i) => (
              <div
                key={e.toolName}
                className="p-3 rounded-lg border space-y-3 bg-card"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-xs font-mono text-muted-foreground">
                    {e.toolName}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs" htmlFor={`mcp-entry-slug-${i}`}>
                      Tenant slug<span className="text-red-500 ml-0.5">*</span>
                    </Label>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="secondary"
                        className="text-[10px] font-mono shrink-0"
                      >
                        ext.
                      </Badge>
                      <Input
                        id={`mcp-entry-slug-${i}`}
                        value={e.slug}
                        onChange={(ev) =>
                          updateEntry(i, { slug: slugify(ev.target.value) })
                        }
                        className="font-mono text-xs"
                        maxLength={50}
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs" htmlFor={`mcp-entry-name-${i}`}>
                      Display name<span className="text-red-500 ml-0.5">*</span>
                    </Label>
                    <Input
                      id={`mcp-entry-name-${i}`}
                      value={e.displayName}
                      onChange={(ev) =>
                        updateEntry(i, { displayName: ev.target.value })
                      }
                      maxLength={60}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={e.description}
                    onChange={(ev) =>
                      updateEntry(i, { description: ev.target.value })
                    }
                    className="text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Execution policy</Label>
                  <RadioGroup
                    value={e.policy}
                    onValueChange={(v) =>
                      updateEntry(i, { policy: v as ToolPolicy })
                    }
                    className="grid grid-cols-3 gap-2"
                  >
                    {(['auto_approve', 'require_approval', 'forbidden'] as ToolPolicy[]).map(
                      (p) => (
                        <label
                          key={p}
                          htmlFor={`mcp-entry-policy-${i}-${p}`}
                          className={cn(
                            'flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors',
                            e.policy === p
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/30',
                          )}
                        >
                          <RadioGroupItem
                            id={`mcp-entry-policy-${i}-${p}`}
                            value={p}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <PolicyBadge policy={p} />
                            <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                              {POLICY_DESCRIPTIONS[p]}
                            </p>
                          </div>
                        </label>
                      ),
                    )}
                  </RadioGroup>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
